import { useEffect, useState } from "react";

/**
 * Slider de faixa com dois polegares (mínimo/máximo), construído sobre dois
 * <input type="range"> sobrepostos. Controlado: recebe `value` [lo, hi] e
 * dispara `onChange([lo, hi])`.
 *
 * Com `inputs`, exibe campos de valor inicial/final abaixo do trilho —
 * digitação livre, aplicada no blur/Enter (com clamp aos limites). `scale`
 * converte a unidade interna para a exibida (ex.: 100 = centavos → reais)
 * e `prefix` antecede os campos (ex.: "R$"). Sem `inputs`, mostra os
 * rótulos formatados por `format`.
 */
export default function RangeSlider({ min = 0, max = 100, step = 1, value, onChange, format, inputs = false, scale = 1, prefix }) {
    const safeMax = max > min ? max : min + 1;
    const [lo, hi] = value ?? [min, safeMax];

    const range = safeMax - min || 1;
    const loPct = ((lo - min) / range) * 100;
    const hiPct = ((hi - min) / range) * 100;

    const toDisplay = (v) => {
        const d = v / scale;
        const s = Number.isInteger(d) ? String(d) : String(Math.round(d * 100) / 100);
        return s.replace('.', ',');
    };

    // Campos digitáveis: estado local, aplicado no blur/Enter.
    const [loStr, setLoStr] = useState(toDisplay(lo));
    const [hiStr, setHiStr] = useState(toDisplay(hi));

    useEffect(() => {
        setLoStr(toDisplay(lo));
        setHiStr(toDisplay(hi));
    }, [lo, hi, scale]);

    const handleLo = (e) => {
        const v = Math.min(Number(e.target.value), hi);
        onChange([v, hi]);
    };

    const handleHi = (e) => {
        const v = Math.max(Number(e.target.value), lo);
        onChange([lo, v]);
    };

    const clamp = (v) => Math.min(Math.max(v, min), safeMax);

    const commitLo = () => {
        const parsed = parseFloat(String(loStr).replace(',', '.'));
        if (isNaN(parsed)) { setLoStr(toDisplay(lo)); return; }
        const v = Math.min(clamp(Math.round(parsed * scale)), hi);
        onChange([v, hi]);
        setLoStr(toDisplay(v));
    };

    const commitHi = () => {
        const parsed = parseFloat(String(hiStr).replace(',', '.'));
        if (isNaN(parsed)) { setHiStr(toDisplay(hi)); return; }
        const v = Math.max(clamp(Math.round(parsed * scale)), lo);
        onChange([lo, v]);
        setHiStr(toDisplay(v));
    };

    const commitOnEnter = (commit) => (e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); }
    };

    const fmt = (v) => (format ? format(v) : v);

    return (
        <div className="range-slider">
            <div className="range-slider__rail">
                <div
                    className="range-slider__fill"
                    style={{ left: `${loPct}%`, right: `${100 - hiPct}%` }}
                />
            </div>
            <input
                type="range"
                className="range-slider__thumb range-slider__thumb--lo"
                min={min}
                max={safeMax}
                step={step}
                value={lo}
                onChange={handleLo}
                aria-label="Valor mínimo"
            />
            <input
                type="range"
                className="range-slider__thumb range-slider__thumb--hi"
                min={min}
                max={safeMax}
                step={step}
                value={hi}
                onChange={handleHi}
                aria-label="Valor máximo"
            />
            {inputs ? (
                <div className="range-slider__inputs">
                    <label className="range-slider__input-wrap">
                        {prefix && <span className="range-slider__prefix">{prefix}</span>}
                        <input
                            type="text"
                            inputMode="decimal"
                            value={loStr}
                            onChange={(e) => setLoStr(e.target.value)}
                            onBlur={commitLo}
                            onKeyDown={commitOnEnter(commitLo)}
                            aria-label="Valor inicial"
                        />
                    </label>
                    <span className="range-slider__sep">–</span>
                    <label className="range-slider__input-wrap">
                        {prefix && <span className="range-slider__prefix">{prefix}</span>}
                        <input
                            type="text"
                            inputMode="decimal"
                            value={hiStr}
                            onChange={(e) => setHiStr(e.target.value)}
                            onBlur={commitHi}
                            onKeyDown={commitOnEnter(commitHi)}
                            aria-label="Valor final"
                        />
                    </label>
                </div>
            ) : (
                <div className="range-slider__values">
                    <span>{fmt(lo)}</span>
                    <span>{fmt(hi)}</span>
                </div>
            )}
        </div>
    );
}
